import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import {
  closeConsentSettings,
  initConsent,
  openConsentSettings,
  setConsent,
  useConsent,
  useConsentSettingsOpen,
} from "@/lib/consent";

/**
 * Custom cookie-/toestemmingsbanner (AVG + ePrivacy). Verschijnt bij het eerste
 * bezoek; "Alles accepteren" en "Alleen noodzakelijk" staan even prominent
 * (weigeren moet net zo makkelijk zijn als accepteren). Via "Instellingen" of de
 * footer-link "Cookie-instellingen" kun je per categorie kiezen en later wijzigen.
 */
export function CookieBanner() {
  const t = useT();
  const choice = useConsent();
  const settingsOpen = useConsentSettingsOpen();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // Consent-default zetten + eerdere keuze toepassen (één keer, client-side).
  useEffect(() => { initConsent(); }, []);

  // Bij openen van het paneel de schakelaars op de huidige keuze zetten.
  useEffect(() => {
    if (settingsOpen) {
      setAnalytics(choice?.analytics ?? false);
      setMarketing(choice?.marketing ?? false);
    }
  }, [settingsOpen, choice]);

  const showBanner = choice === null && !settingsOpen;
  /**
   * De banner staat `fixed bottom-0` en ligt daarmee precies over de actiebalken van de site —
   * de koopbalk op een productpagina (StickyBuyBar) en de afrekenbalk in de winkelwagen, die
   * allebei óók onderaan plakken maar een laag lager liggen.
   *
   * Op een telefoon beslaat deze banner ongeveer een derde van het scherm. Daardoor was
   * "In winkelwagen" en "Afrekenen" op mobiel letterlijk niet aan te klikken zolang er geen
   * cookiekeuze was gemaakt. In GA4 zag je dat terug: mobiel legde twee keer zoveel in het mandje
   * als desktop (117 tegen 52) maar begon half zo vaak met afrekenen (22% tegen 46%).
   *
   * Daarom geeft de banner zijn eigen hoogte door als `--consent-h`. De actiebalken schuiven
   * daarmee omhoog zolang hij er staat, en zakken terug zodra er gekozen is. Eén variabele, zodat
   * er geen tweede plek is die de hoogte moet weten. (27-10-2026.)
   */
  const bannerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!showBanner) { root.style.removeProperty("--consent-h"); return; }
    const meet = (): void => {
      const h = bannerRef.current?.offsetHeight ?? 0;
      root.style.setProperty("--consent-h", `${h}px`);
    };
    meet();
    const ro = new ResizeObserver(meet);
    if (bannerRef.current) ro.observe(bannerRef.current);
    window.addEventListener("resize", meet);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", meet);
      root.style.removeProperty("--consent-h");
    };
  }, [showBanner]);

  if (!showBanner && !settingsOpen) return null;

  return (
    <>
      {/* Onderste banner — alleen vóór een keuze, en als het paneel dicht is. Vaste id (niet
          taalgevoelig zoals aria-label): scripts/prerender.mjs knipt 'm er via dit id uit —
          de statische snapshot mag nooit "geen toestemming" laten zien aan een bezoeker die
          al eerder wél koos. */}
      {showBanner && (
        <div
          ref={bannerRef}
          id="cookie-banner"
          role="dialog"
          aria-label={t("Cookietoestemming")}
          aria-live="polite"
          className="fixed inset-x-0 bottom-0 z-[60] border-t border-primary-200 bg-white/98 px-4 py-4 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] backdrop-blur"
        >
          <div className="mx-auto flex max-w-[1100px] flex-col items-start gap-4 md:flex-row md:items-center">
            <div className="min-w-0 flex-1 text-sm text-primary-700">
              <p className="font-semibold text-primary-900">{t("We gebruiken cookies 🍪")}</p>
              <p className="mt-0.5 leading-snug">
                {t("Noodzakelijke cookies houden de site veilig en werkend. Met uw toestemming gebruiken we ook analytische cookies om de site te verbeteren.")}{" "}
                <Link to="/cookies" className="font-medium text-accent-700 underline hover:text-accent-700">
                  {t("Lees ons cookiebeleid")}
                </Link>
                .
              </p>
            </div>
            <div className="flex w-full shrink-0 flex-wrap items-center gap-2 md:w-auto">
              <button
                type="button"
                onClick={() => setConsent(false, false)}
                className="min-h-11 flex-1 rounded-lg border border-primary-300 px-4 text-sm font-semibold text-primary-700 transition hover:bg-primary-50 md:flex-none"
              >
                {t("Alleen noodzakelijk")}
              </button>
              <button
                type="button"
                onClick={() => setConsent(true, true)}
                className="min-h-11 flex-1 rounded-lg bg-accent-700 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-accent-800 md:flex-none"
              >
                {t("Alles accepteren")}
              </button>
              <button
                type="button"
                onClick={() => { setAnalytics(false); setMarketing(false); openConsentSettings(); }}
                className="min-h-11 w-full px-2 text-sm font-medium text-primary-600 underline transition hover:text-primary-900 md:w-auto"
              >
                {t("Instellingen")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instellingen-paneel — per categorie kiezen (ook later via de footer). */}
      {settingsOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t("Cookie-instellingen")}
          className="fixed inset-0 z-[70] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
          onClick={closeConsentSettings}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-primary-100 p-5">
              <h2 className="text-lg font-bold text-primary-900">{t("Cookie-instellingen")}</h2>
              <button
                type="button"
                onClick={closeConsentSettings}
                aria-label={t("Sluiten")}
                className="-mr-1 rounded-lg px-2 py-1 text-xl leading-none text-primary-600 hover:bg-primary-50 hover:text-primary-800"
              >
                ✕
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
              <p className="text-sm text-primary-600">
                {t("Kies welke cookies we mogen gebruiken. U kunt dit altijd later aanpassen via \"Cookie-instellingen\" in de footer.")}
              </p>

              {/* Noodzakelijk — altijd aan */}
              <div className="rounded-xl border border-primary-100 bg-primary-50 p-4">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold text-primary-900">{t("Noodzakelijk")}</span>
                  <span className="rounded-full bg-primary-200 px-2.5 py-0.5 text-xs font-semibold text-primary-700">{t("Altijd actief")}</span>
                </div>
                <p className="mt-1 text-sm text-primary-600">
                  {t("Nodig voor basisfuncties: winkelwagen, inloggen, voorkeuren en beveiliging (bot-bescherming). Hiervoor is geen toestemming vereist.")}
                </p>
              </div>

              {/* Analytisch — schakelbaar */}
              <label className="flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-primary-200 p-4 transition hover:border-primary-300">
                <span>
                  <span className="block font-semibold text-primary-900">{t("Analytisch")}</span>
                  <span className="mt-1 block text-sm text-primary-600">
                    {t("Anonieme bezoekersstatistieken (Google Analytics) om de site te verbeteren. Wordt pas geladen ná uw toestemming.")}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 accent-accent-500"
                />
              </label>

              {/* Marketing — schakelbaar */}
              <label className="flex cursor-pointer items-start justify-between gap-3 rounded-xl border border-primary-200 p-4 transition hover:border-primary-300">
                <span>
                  <span className="block font-semibold text-primary-900">{t("Marketing")}</span>
                  <span className="mt-1 block text-sm text-primary-600">
                    {t("Cookies voor advertenties en remarketing (bv. Google Ads, Meta), zodat u relevantere aanbiedingen ziet. Worden pas geplaatst ná uw toestemming.")}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="mt-1 h-5 w-5 shrink-0 accent-accent-500"
                />
              </label>
            </div>

            <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-primary-100 p-5">
              <button
                type="button"
                onClick={() => setConsent(analytics, marketing)}
                className="min-h-11 rounded-lg border border-primary-300 px-5 text-sm font-semibold text-primary-700 transition hover:bg-primary-50"
              >
                {t("Keuze opslaan")}
              </button>
              <button
                type="button"
                onClick={() => setConsent(true, true)}
                className="min-h-11 rounded-lg bg-accent-700 px-6 text-sm font-bold text-white shadow-sm transition hover:bg-accent-800"
              >
                {t("Alles accepteren")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
